import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import JSZip from 'jszip';
import { auth } from '../../firebase/config';
import {
  GROUP_PLAN_PRICES,
  calculateGroupPrice,
  getGroupDiscountTier,
  getGroupPricingRows,
} from '../../utils/groupPricing';
import {
  addAdminOperationNote,
  createAdminOperationCase,
  deleteAdminOperationCase,
  deleteAdminOperationNote,
  ensureAdminOperationPaymentLink,
  getAdminOperationCase,
  getAdminOperationCases,
  issueAdminOperationCoupons,
  previewAdminOperationDocuments,
  resendAdminOperationCouponEmail,
  sendAdminOperationDocumentsEmail,
  sendAdminOperationPaymentLinkEmail,
  updateAdminOperationCase,
  updateAdminOperationNote,
  updateAdminOperationTask,
} from '../../api/adminService';
import { useToast } from '../../components/Toast';
import AdminManagedDocumentsPanel from './AdminManagedDocumentsPanel';
import './AdminOperationsPage.css';

const STATUS_OPTIONS = [
  { key: 'in_progress', label: '진행 중' },
  { key: 'done', label: '완료' },
  { key: 'all', label: '전체' },
];

const SOURCE_OPTIONS = [
  { key: 'staff', label: '직접 등록' },
  { key: 'site_quote', label: '사이트 서류 요청' },
];

const SITE_QUOTE_SOURCE = 'site_quote';

const getCaseSourceKey = (caseItem) => (caseItem?.source === SITE_QUOTE_SOURCE ? SITE_QUOTE_SOURCE : 'staff');

const DEFAULT_NEXT_ACTION = '요청받은 서류와 다음 처리 내용을 확인해주세요.';

const SIGNATURE_PROFILES = {
  'hsmroof@gmail.com': { name: '강연구', role: 'CEO' },
  'blackwolf1223@gmail.com': { name: '박성현', role: 'AI연구소장' },
  'blackwolfdev1223@gmail.com': { name: '박성현', role: 'AI연구소장' },
  'jungame3757@gmail.com': { name: '서준', role: 'CTO' },
};

const getSignatureProfile = (email = '') => (
  SIGNATURE_PROFILES[String(email).trim().toLowerCase()] || { name: '왓퀴즈AI', role: '운영팀' }
);

const STANDARD_PLAN_IDS_BY_MONTH = {
  1: '1month',
  3: '3month',
  6: '6month',
  12: '12month',
};

const normalizeDurationMonths = (months, fallback = 12) => {
  const parsed = Number(months);
  if (!Number.isFinite(parsed) || parsed < 1) return fallback;
  return Math.max(1, Math.min(24, Math.floor(parsed)));
};

const LEGACY_STATUS_LABELS = {
  new: '진행 중',
  active: '진행 중',
  quoted: '진행 중',
  docs_sent: '진행 중',
  coupon_sent: '진행 중',
  s2b_waiting: '진행 중',
  inspection_waiting: '진행 중',
  tax_invoice_waiting: '진행 중',
  blocked: '진행 중',
};

const STATUS_LABELS = STATUS_OPTIONS.reduce(
  (acc, item) => ({ ...acc, [item.key]: item.label }),
  LEGACY_STATUS_LABELS
);

const getPlanIdFromMonths = (months) => {
  const parsed = normalizeDurationMonths(months);
  return STANDARD_PLAN_IDS_BY_MONTH[parsed] || '1month';
};

const getProductLabel = (months) => `PRO ${normalizeDurationMonths(months)}개월권`;
const getFormTotal = (values) => Number(values.total_amount || 0)
  || Number(values.unit_price || 0) * Number(values.quantity || 1);
const getDerivedUnitPrice = (values) => Math.round(getFormTotal(values) / Math.max(1, Number(values.quantity || 1)));
const getPolicyUnitPrice = (months, quantity = 1) => {
  const parsed = normalizeDurationMonths(months);
  const qty = Math.max(1, Number(quantity || 1));
  const planId = STANDARD_PLAN_IDS_BY_MONTH[parsed];
  if (planId) return calculateGroupPrice(planId, qty).unitPrice;
  const tier = getGroupDiscountTier(qty);
  const monthlyPrice = GROUP_PLAN_PRICES['1month']?.price || 24000;
  return Math.round(monthlyPrice * parsed * (1 - tier.discountRate));
};
const getSuggestedTotal = (months, quantity = 1) => getPolicyUnitPrice(months, quantity) * Math.max(1, Number(quantity || 1));
const getDiscountLabel = (quantity = 1) => {
  const tier = getGroupDiscountTier(quantity);
  const discountPercent = Math.round(tier.discountRate * 100);
  return discountPercent > 0 ? `${tier.label} ${discountPercent}% 할인` : `${tier.label} 할인 없음`;
};
const isPolicySuggestedTotal = (values = {}) => (
  Number(values.total_amount || 0) === getSuggestedTotal(values.duration_months || 12, values.quantity || 1)
);
const isPdfDocument = (doc) => (doc?.content_type || 'application/pdf') === 'application/pdf';
const getDocumentPreviewSrc = (doc) => (
  `data:${doc.content_type || 'application/pdf'};base64,${doc.content}#toolbar=1&navpanes=0&view=FitH`
);

const DOCUMENT_OPTIONS = [
  { key: 'quotation', label: '견적서' },
  { key: 'purchase_draft', label: '구매 품의서' },
  { key: 'official_letter', label: '공문/기안문' },
  { key: 'delivery_note', label: '납품서/주문확인서' },
  { key: 'transaction_statement', label: '거래명세서' },
  { key: 'static_docs', label: '행정 서류 5종' },
  { key: 'insurance_certificate', label: '4대보험완납증명서' },
  { key: 'user_manual', label: '선생님 사용 매뉴얼' },
];

// 납품서·거래명세서는 결제 뒤 서류라 기본 선택에서 뺀다('납품서 요청 답변 채우기'로 따로 보낸다).
const PRE_PURCHASE_DOCUMENT_TYPES = ['quotation', 'purchase_draft', 'official_letter', 'static_docs', 'insurance_certificate', 'user_manual'];

const COUPON_MONTH_OPTIONS = [1, 2, 3, 6, 9, 12, 24];

const getTodayDateString = () => {
  const now = new Date();
  const offset = now.getTimezoneOffset();
  return new Date(now.getTime() - offset * 60 * 1000).toISOString().slice(0, 10);
};

const CHECKLIST_TASK_IDS = new Set(['negotiation_accepted', 's2b_accepted', 'coupon_sent', 'tax_invoice_issued']);
const VISIBLE_TIMELINE_TYPES = new Set([
  'note_added',
  'note_updated',
  'note_deleted',
  'email_received',
  'email_sent',
  'coupons_issued',
  'note_todo_updated',
  'task_updated',
]);

const emptyForm = {
  organization: '',
  contact_name: '',
  contact_email: '',
  cc_emails: [],
  contact_phone: '',
  duration_months: 12,
  quantity: 1,
  unit_price: getPolicyUnitPrice(12, 1),
  total_amount: getSuggestedTotal(12, 1),
  contract_date: getTodayDateString(),
  split_policy: '',
  memo_summary: '',
  next_action: DEFAULT_NEXT_ACTION,
};

const getRecipientFormFromCase = (caseData = {}) => ({
  contact_name: caseData.contact_name || '',
  contact_email: caseData.contact_email || '',
  cc_emails: (caseData.cc_emails || []).join('\n'),
  contact_phone: caseData.contact_phone || '',
  contract_date: caseData.contract_date || getTodayDateString(),
  duration_months: caseData.duration_months || 12,
  quantity: caseData.quantity || 1,
  unit_price: caseData.unit_price || getPolicyUnitPrice(caseData.duration_months || 12, caseData.quantity || 1),
  total_amount: caseData.total_amount || getSuggestedTotal(caseData.duration_months || 12, caseData.quantity || 1),
  split_policy: caseData.split_policy || '',
  next_action: caseData.next_action || DEFAULT_NEXT_ACTION,
});

const getDefaultCouponFormFromCase = (caseData = {}) => ({
  seriesName: '',
  benefitMonths: caseData.duration_months || 12,
  count: '',
  maxUsesPerCoupon: 1,
  description: `${caseData.organization || '기관'} PRO ${caseData.duration_months || 12}개월권`,
  sendEmail: true,
});

const parseEmailList = (value = '') => String(value || '')
  .split(/[\n,;]+/)
  .map((item) => item.trim())
  .filter(Boolean);

const getRecipientPayloadFromForm = (draft = {}) => ({
  ...draft,
  cc_emails: parseEmailList(draft.cc_emails),
  plan_id: getPlanIdFromMonths(draft.duration_months),
  contract_date: draft.contract_date || getTodayDateString(),
  duration_months: normalizeDurationMonths(draft.duration_months),
  quantity: Number(draft.quantity || 1),
  unit_price: getDerivedUnitPrice(draft),
  total_amount: getFormTotal(draft),
  next_action: draft.next_action || DEFAULT_NEXT_ACTION,
});

const buildDocsTemplate = (caseData, mode = 'default') => {
  const org = caseData?.organization || '학교';
  const name = caseData?.contact_name || '담당자';
  if (mode === 'quotationOnly') {
    return {
      subject: `[왓퀴즈AI] ${org} 견적서 보내드립니다`,
      body: `안녕하세요, ${name}님.\n\n요청 주신 ${org} 왓퀴즈AI 견적서를 첨부해 드립니다.\n\nS2B 학교장터 수의시담으로 진행하실 경우 수취인은 쿠폰을 받으실 대표 선생님 성함과 이메일로 지정해 주세요.\n\n학교 카드로 바로 결제하시려면 메일 아래 '카드 결제 페이지 열기' 버튼을 눌러 주세요. 인원이나 기간이 바뀌면 '견적서 직접 만들기'에서 견적서를 바로 다시 받으실 수 있어요.\n\n견적 인원, 기간, 금액을 확인해주시고 수정이 필요하시면 편하게 회신 부탁드립니다.\n\n감사합니다.\n왓퀴즈AI 드림`,
      documentTypes: ['quotation'],
    };
  }
  if (mode === 'delivery') {
    return {
      subject: `[왓퀴즈AI] ${org} 납품 서류 보내드립니다`,
      body: `안녕하세요, ${name}님.\n\n요청 주신 ${org} 왓퀴즈AI 납품서(주문확인서)와 거래명세서를 첨부해 드립니다.\n\n선생님들께서 가입부터 쿠폰 등록, 기능별 사용법까지 따라 하실 수 있도록 선생님 사용 매뉴얼도 함께 첨부했어요. 쿠폰과 함께 전달해 주세요.\n\n검수나 회계 처리 과정에서 추가 양식이 필요하시면 본 메일로 회신 부탁드립니다.\n\n감사합니다.\n왓퀴즈AI 드림`,
      documentTypes: ['delivery_note', 'transaction_statement', 'user_manual'],
    };
  }
  if (mode === 'quoteChange') {
    return {
      subject: `[왓퀴즈AI] ${org} 수정 견적서 보내드립니다`,
      body: `안녕하세요, ${name}님.\n\n말씀 주신 조건을 반영해 ${org} 왓퀴즈AI 견적서와 행정 서류를 다시 첨부해 드립니다.\n\nS2B 학교장터 수의시담으로 진행하실 경우 수취인은 쿠폰을 받으실 대표 선생님 성함과 이메일로 지정해 주세요.\n\n학교 카드로 바로 결제하시려면 메일 아래 '카드 결제 페이지 열기' 버튼을 눌러 주세요. 인원이나 기간이 바뀌면 '견적서 직접 만들기'에서 견적서를 바로 다시 받으실 수 있어요.\n\n견적 인원, 기간, 금액을 확인해주시고 추가 수정이 필요하시면 편하게 회신 부탁드립니다.\n\n감사합니다.\n왓퀴즈AI 드림`,
      documentTypes: ['quotation', 'static_docs', 'user_manual'],
    };
  }
  return {
    subject: `[왓퀴즈AI] ${org} 구매 서류 보내드립니다`,
    body: `안녕하세요, ${name}님.\n\n요청 주신 왓퀴즈AI 견적서와 학교 품의에 필요한 행정 서류를 첨부해 드립니다.\n\n첨부 서류에는 견적서, 구매 품의서, 공문/기안문, 서비스 소개서, 사업자등록증, 통장사본, 학습지원 SW 필수기준 체크리스트, 개인정보 수집·이용 동의서, 4대보험완납증명서가 포함되어 있습니다. 선생님들께 전달하실 선생님 사용 매뉴얼도 함께 첨부했어요.\n\n왓퀴즈AI는 한국교육학술정보원(KERIS) 에듀집 검토 결과 학교운영위원회 심의 대상이 아니라서(2026. 9. 22. 반영) 품의만 올리시면 됩니다.\n\nS2B 학교장터 수의시담으로 진행하실 경우 수취인은 쿠폰을 받으실 대표 선생님 성함과 이메일로 지정해 주세요.\n\n학교 카드로 바로 결제하시려면 메일 아래 '카드 결제 페이지 열기' 버튼을 눌러 주세요. 인원이나 기간이 바뀌면 '견적서 직접 만들기'에서 견적서를 바로 다시 받으실 수 있어요.\n\n품의서와 공문은 학교 내부 결재에 그대로 첨부하거나, 학교 양식에 맞춰 제출하실 때 활용하시면 됩니다.\n\n내용 수정이나 추가 서류가 필요하시면 본 메일로 회신 부탁드립니다.\n\n감사합니다.\n왓퀴즈AI 드림`,
    documentTypes: PRE_PURCHASE_DOCUMENT_TYPES,
  };
};

// 사업자등록증·통장사본·4대보험·매뉴얼은 결제 전후 모두 보내므로 판단에 쓰지 않는다(백엔드 _is_delivery_package와 같은 기준).
const isDeliveryPackage = (rawTypes = []) => {
  const types = rawTypes.map((type) => String(type || '').replace(/_hwpx$/, ''));
  return types.some((type) => ['delivery_note', 'transaction_statement'].includes(type))
    && !types.some((type) => ['quotation', 'purchase_draft', 'official_letter', 'internal_draft'].includes(type));
};

const getDocsStageWarning = (types = [], form = {}) => {
  const hasDelivery = types.some((type) => ['delivery_note', 'transaction_statement'].includes(type));
  if (!hasDelivery) return '';
  if (!isDeliveryPackage(types)) {
    return '결제 전 서류(견적서·품의서·공문)와 납품 서류가 함께 선택돼 있어요. 결제 전 메일로 보내져 카드 결제 버튼이 들어갑니다.';
  }
  const text = `${form.subject || ''}\n${form.body || ''}`;
  if (['구매 서류', 'S2B 학교장터', '카드 결제'].some((phrase) => text.includes(phrase))) {
    return "납품 서류만 보내는데 제목·본문에 구매 안내 문구가 남아 있어요. '납품서 요청 답변 채우기'로 다시 채울 수 있어요.";
  }
  return '';
};

const buildGeneratedDocsEmail = (caseData, documents = []) => {
  const org = caseData?.organization || '학교';
  const name = caseData?.contact_name || '담당자';
  const labels = documents.map((doc) => doc.label).filter(Boolean);
  const docSummary = '구매 서류';
  const attachmentLines = labels.length > 0
    ? labels.map((label) => `- ${label}`).join('\n')
    : '- 요청 서류';

  if (isDeliveryPackage(documents.map((doc) => doc.type))) {
    return {
      subject: `[왓퀴즈AI] ${org} 납품 서류 보내드립니다`,
      body: `안녕하세요, ${name}님.\n\n요청 주신 ${org} 왓퀴즈AI 납품 서류를 첨부해 드립니다.\n\n첨부 서류\n${attachmentLines}\n\n검수나 회계 처리 과정에서 추가 양식이 필요하시면 본 메일로 회신 부탁드립니다.\n\n감사합니다.\n왓퀴즈AI 드림`,
    };
  }

  return {
    subject: `[왓퀴즈AI] ${org} 구매 서류 보내드립니다`,
    body: `안녕하세요, ${name}님.\n\n요청 주신 왓퀴즈AI ${docSummary}를 첨부해 드립니다.\n\n첨부 서류\n${attachmentLines}\n\nS2B 학교장터 수의시담으로 진행하실 경우 수취인은 쿠폰을 받으실 대표 선생님 성함과 이메일로 지정해 주세요.\n\n학교 카드로 바로 결제하시려면 메일 아래 '카드 결제 페이지 열기' 버튼을 눌러 주세요. 인원이나 기간이 바뀌면 '견적서 직접 만들기'에서 견적서를 바로 다시 받으실 수 있어요.\n\n내용 확인 후 추가 서류나 수정이 필요하시면 편하게 회신 부탁드립니다.\n\n감사합니다.\n왓퀴즈AI 드림`,
  };
};

const fmtMoney = (value) => `₩${Number(value || 0).toLocaleString()}`;

const formatDate = (value) => {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleString('ko-KR', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return value;
  }
};

const formatContractDate = (value) => {
  if (!value) return '계약일 없음';
  try {
    return new Date(`${value}T00:00:00`).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return value;
  }
};

const getTimelineTime = (item) => item?.created_at || item?.sent_at || item?.received_at || item?.at;

const getCaseTodoStats = (caseItem) => {
  if (!caseItem) return { done: 0, total: 0, isComplete: false };
  const isCaseDone = (caseItem.effective_status || caseItem.status) === 'done';
  if (caseItem.task_todo_total !== undefined || caseItem.note_todo_total !== undefined) {
    const taskTotal = Number(caseItem.task_todo_total || CHECKLIST_TASK_IDS.size);
    const taskDone = Number(caseItem.task_todo_done || 0);
    const noteTotal = Number(caseItem.note_todo_total || 0);
    const noteDone = Number(caseItem.note_todo_done || 0);
    const total = taskTotal + noteTotal;
    const done = taskDone + noteDone;
    return { done, total, isComplete: isCaseDone || (total > 0 && done === total) };
  }
  const aggregatedTotal = Number(caseItem.todo_total || 0);
  if (aggregatedTotal > 0) {
    const aggregatedDone = Number(caseItem.todo_done || 0);
    return {
      done: aggregatedDone,
      total: aggregatedTotal,
      isComplete: isCaseDone || aggregatedDone === aggregatedTotal,
    };
  }
  if (Array.isArray(caseItem.tasks) || Array.isArray(caseItem.notes)) {
    const tasks = Array.isArray(caseItem.tasks) ? caseItem.tasks : [];
    const trackedTasks = tasks.filter((task) => CHECKLIST_TASK_IDS.has(task.id));
    const taskTotal = trackedTasks.length || CHECKLIST_TASK_IDS.size;
    const taskDone = trackedTasks.filter((task) => task.completed).length;
    const notes = Array.isArray(caseItem.notes) ? caseItem.notes : [];
    const noteTotal = notes.length;
    const noteDone = notes.filter((note) => note.completed).length;
    const total = taskTotal + noteTotal;
    const done = taskDone + noteDone;
    return { done, total, isComplete: isCaseDone || (total > 0 && done === total) };
  }
  const total = CHECKLIST_TASK_IDS.size;
  const done = 0;
  return { done, total, isComplete: isCaseDone || (total > 0 && done === total) };
};

const getSelectedDocumentLabels = (documentTypes) => DOCUMENT_OPTIONS
  .filter((doc) => documentTypes.includes(doc.key))
  .map((doc) => doc.label)
  .join(', ');

const CaseCardSkeleton = () => (
  <div className="admin-ops__case-card admin-ops__case-card--skeleton" aria-hidden="true">
    <div className="admin-ops__skeleton admin-ops__skeleton--badge" />
    <div className="admin-ops__skeleton admin-ops__skeleton--title" />
    <div className="admin-ops__skeleton admin-ops__skeleton--line" />
    <div className="admin-ops__skeleton admin-ops__skeleton--line admin-ops__skeleton--short" />
  </div>
);

const CaseListSkeleton = ({ count = 4 }) => (
  <>
    {Array.from({ length: count }).map((_, index) => (
      <CaseCardSkeleton key={`case-skeleton-${index}`} />
    ))}
  </>
);

const DetailSkeleton = () => (
  <div className="admin-ops__detail-skeleton" aria-hidden="true">
    <div className="admin-ops__skeleton admin-ops__skeleton--badge" />
    <div className="admin-ops__skeleton admin-ops__skeleton--detail-title" />
    <div className="admin-ops__skeleton admin-ops__skeleton--line" />
    <div className="admin-ops__skeleton-grid">
      {[0, 1, 2, 3].map((item) => (
        <div key={item} className="admin-ops__skeleton-card">
          <div className="admin-ops__skeleton admin-ops__skeleton--short" />
          <div className="admin-ops__skeleton admin-ops__skeleton--line" />
        </div>
      ))}
    </div>
    <div className="admin-ops__skeleton-card admin-ops__skeleton-card--large">
      <div className="admin-ops__skeleton admin-ops__skeleton--title" />
      <div className="admin-ops__skeleton admin-ops__skeleton--line" />
      <div className="admin-ops__skeleton admin-ops__skeleton--line" />
    </div>
  </div>
);

const SectionLoading = ({ message = '처리 중이에요' }) => (
  <div className="admin-ops__section-loading" role="status" aria-live="polite">
    <div className="admin-ops__section-loading-copy">{message}</div>
    <div className="admin-ops__skeleton admin-ops__skeleton--line" />
    <div className="admin-ops__skeleton admin-ops__skeleton--line admin-ops__skeleton--short" />
  </div>
);

const isCaseInStatusFilter = (caseItem, statusFilter) => {
  if (!caseItem || statusFilter === 'all') return Boolean(caseItem);
  return (caseItem.effective_status || caseItem.status) === statusFilter;
};

const getBusyMessage = (busyAction) => {
  if (!busyAction) return '';
  if (busyAction === 'recipient') return '수신자 정보를 저장하고 있어요';
  if (busyAction === 'docs-preview') return '문서 패키지를 만들고 있어요';
  if (busyAction === 'docs-send') return '메일을 발송하고 있어요';
  if (busyAction === 'coupon-preview') return '쿠폰 발급 범위를 확인하고 있어요';
  if (busyAction === 'coupon') return '쿠폰을 발급하고 있어요';
  if (busyAction === 'coupon-resend') return '쿠폰 안내 메일을 다시 보내고 있어요';
  if (busyAction === 'note') return '메모를 저장하고 있어요';
  if (busyAction.startsWith('note-')) return '메모를 반영하고 있어요';
  if (busyAction.startsWith('task-')) return '체크리스트를 저장하고 있어요';
  if (busyAction === 'delete') return '업무 케이스를 삭제하고 있어요';
  if (busyAction === 'paylink') return '카드 결제 링크를 준비하고 있어요';
  if (busyAction === 'paylink-send') return '결제 링크 메일을 보내고 있어요';
  return '처리 중이에요';
};

const getPaymentLinkState = (caseData) => {
  const link = caseData?.payment_link;
  if (!link?.token) return { kind: 'none' };
  if (link.status === 'paid') return { kind: 'paid', link };
  const expired = link.expires_at && new Date(link.expires_at).getTime() <= Date.now();
  const termsChanged = Number(link.total_amount) !== Number(caseData.total_amount || 0)
    || Number(link.quantity) !== Number(caseData.quantity || 0)
    || Number(link.duration_months) !== Number(caseData.duration_months || 0);
  if (link.status !== 'active' || expired || termsChanged) return { kind: 'stale', link, termsChanged };
  return { kind: 'active', link };
};

const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
};

const SignaturePreview = ({ senderEmail }) => {
  const profile = getSignatureProfile(senderEmail);
  return (
    <div className="admin-ops__signature-preview">
      <div className="admin-ops__signature-title">
        <span>메일 서명 미리보기</span>
        <small>{senderEmail || '처리자 이메일 없음'}</small>
      </div>
      <div className="admin-ops__signature-card">
        <strong>{profile.name}</strong>
        <span>플랙티컬(주) / {profile.role}</span>
        <p>
          서울특별시 노원구 공릉로 232, 제1창업보육센터 404호 (공릉동, 서울과학기술대학교)<br />
          T 02-949-9158<br />
          Email angel@plactical.com<br />
          Homepage https://whatquiz.co.kr/
        </p>
      </div>
    </div>
  );
};

const RecipientEmailHelp = () => (
  <span className="admin-ops__help">
    <button
      type="button"
      className="admin-ops__help-trigger"
      aria-label="받는 이메일 안내"
    >
      ?
    </button>
    <span className="admin-ops__help-tooltip" role="tooltip">
      <strong>어떤 이메일을 넣어야 하나요?</strong>
      <span>행정실 이메일이면 쿠폰, 견적서, 세금계산서 안내까지 한 번에 보낼 수 있어요.</span>
      <span>교사 이메일이면 견적서와 안내 메일은 받을 수 있지만, 세금계산서는 행정실 이메일을 받아서 진행해야 해요. 고유번호증에 적힌 이메일을 확인해주세요.</span>
    </span>
  </span>
);

const getTimelineDetailRows = (event) => {
  const metadata = event?.metadata || {};
  const rows = [];
  if (metadata.subject) rows.push(['메일 제목', metadata.subject]);
  if (metadata.to) rows.push(['받는 사람', metadata.to]);
  if (metadata.resend_id) rows.push(['Resend ID', metadata.resend_id]);
  if (metadata.document_types?.length) rows.push(['문서', metadata.document_types.join(', ')]);
  if (metadata.count) rows.push(['개수', `${metadata.count}개`]);
  if (metadata.codes?.length) rows.push(['쿠폰 코드', metadata.codes.join(', ')]);
  if (metadata.prefix) rows.push(['쿠폰 접두사', metadata.prefix]);
  if (metadata.start_suffix) rows.push(['시작 번호', String(metadata.start_suffix).padStart(4, '0')]);
  if (metadata.completed !== undefined) rows.push(['체크 상태', metadata.completed ? '완료' : '대기']);
  if (metadata.fields?.length) rows.push(['수정 항목', metadata.fields.join(', ')]);
  if (metadata.note) rows.push(['메모', metadata.note]);
  if (metadata.previous_note) rows.push(['수정 전 메모', metadata.previous_note]);
  return rows;
};

const downloadBase64 = (doc) => {
  const byteCharacters = atob(doc.content);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i += 1) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const blob = new Blob([new Uint8Array(byteNumbers)], { type: doc.content_type || 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = doc.filename;
  link.click();
  URL.revokeObjectURL(url);
};

const downloadDocumentsZip = async (documents, organization = '왓퀴즈') => {
  const zip = new JSZip();
  documents.forEach((doc) => {
    zip.file(doc.filename, doc.content, { base64: true });
  });
  const blob = await zip.generateAsync({ type: 'blob' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  const cleanOrg = (organization || '왓퀴즈').replace(/[\\/:*?"<>|]/g, '_');
  link.href = url;
  link.download = `왓퀴즈AI_행정서류_${cleanOrg}.zip`;
  link.click();
  URL.revokeObjectURL(url);
};

const AdminOperationsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showToast } = useToast();
  const [user, authLoading] = useAuthState(auth);
  const [status, setStatus] = useState('in_progress');
  const [queryInput, setQueryInput] = useState('');
  const [query, setQuery] = useState('');
  const [cases, setCases] = useState([]);
  const [counts, setCounts] = useState({});
  const [source, setSource] = useState('staff');
  const [sourceCounts, setSourceCounts] = useState({});
  const [selected, setSelected] = useState(null);
  const selectedRef = useRef(null);
  const detailRequestIdRef = useRef(0);
  const listRequestIdRef = useRef(0);
  const ignoredUrlCaseRef = useRef('');
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState({});
  const [form, setForm] = useState({ ...emptyForm, contract_date: getTodayDateString() });
  const [formTotalTouched, setFormTotalTouched] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [noteBody, setNoteBody] = useState('');
  const [editingNoteId, setEditingNoteId] = useState('');
  const [editingNoteBody, setEditingNoteBody] = useState('');
  const [documentResult, setDocumentResult] = useState(null);
  const [selectedDocumentTypes, setSelectedDocumentTypes] = useState(PRE_PURCHASE_DOCUMENT_TYPES);
  const [docsEmailForm, setDocsEmailForm] = useState({ subject: '', body: '' });
  const [showDocsPanel, setShowDocsPanel] = useState(false);
  const [previewModal, setPreviewModal] = useState(null);
  const [recipientForm, setRecipientForm] = useState({
    contact_name: '',
    contact_email: '',
    cc_emails: '',
    contact_phone: '',
    contract_date: getTodayDateString(),
    duration_months: 12,
    quantity: 1,
    unit_price: getPolicyUnitPrice(12, 1),
    total_amount: getSuggestedTotal(12, 1),
    split_policy: '',
    next_action: DEFAULT_NEXT_ACTION,
  });
  const [recipientTotalTouched, setRecipientTotalTouched] = useState(false);
  const [recipientFormDirty, setRecipientFormDirty] = useState(false);
  const [recipientSaveState, setRecipientSaveState] = useState('saved');
  const recipientFormRef = useRef(recipientForm);
  const recipientDirtyRef = useRef(false);
  const recipientCaseIdRef = useRef('');
  const recipientDraftVersionRef = useRef(0);
  const [couponForm, setCouponForm] = useState({
    seriesName: '',
    benefitMonths: 12,
    count: '',
    maxUsesPerCoupon: 1,
    description: '',
    sendEmail: true,
  });
  const [busyAction, setBusyAction] = useState('');
  const [loadingCaseId, setLoadingCaseId] = useState('');

  const busyMessage = useMemo(() => getBusyMessage(busyAction), [busyAction]);
  const isDetailBusy = Boolean(busyAction && busyAction !== 'create');
  const couponMonthOptions = useMemo(() => {
    const currentMonth = parseInt(couponForm.benefitMonths, 10);
    if (!Number.isFinite(currentMonth) || currentMonth < 1) return COUPON_MONTH_OPTIONS;
    return Array.from(new Set([...COUPON_MONTH_OPTIONS, Math.min(24, currentMonth)]))
      .sort((a, b) => a - b);
  }, [couponForm.benefitMonths]);

  useEffect(() => {
    selectedRef.current = selected;
  }, [selected]);

  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(queryInput.trim()), 400);
    return () => window.clearTimeout(timer);
  }, [queryInput]);

  const selectedTotal = useMemo(() => {
    if (!selected) return 0;
    return Number(selected.total_amount || (selected.unit_price || 0) * (selected.quantity || 1));
  }, [selected]);

  const groupPricingRows = useMemo(() => getGroupPricingRows().slice().reverse(), []);
  const formPricing = useMemo(() => ({
    suggestedUnitPrice: getPolicyUnitPrice(form.duration_months, form.quantity),
    suggestedTotal: getSuggestedTotal(form.duration_months, form.quantity),
    enteredTotal: getFormTotal(form),
    derivedUnitPrice: getDerivedUnitPrice(form),
    discountLabel: getDiscountLabel(form.quantity),
  }), [form]);
  const recipientPricing = useMemo(() => ({
    suggestedUnitPrice: getPolicyUnitPrice(recipientForm.duration_months, recipientForm.quantity),
    suggestedTotal: getSuggestedTotal(recipientForm.duration_months, recipientForm.quantity),
    enteredTotal: getFormTotal(recipientForm),
    derivedUnitPrice: getDerivedUnitPrice(recipientForm),
    discountLabel: getDiscountLabel(recipientForm.quantity),
  }), [recipientForm]);
  const recipientSaveCopy = useMemo(() => {
    if (recipientSaveState === 'saving') return '저장 중';
    if (recipientSaveState === 'error') return '저장 실패';
    if (recipientFormDirty || recipientSaveState === 'dirty') return '저장 필요';
    return '저장됨';
  }, [recipientFormDirty, recipientSaveState]);

  const visibleTasks = useMemo(() => (
    (selected?.tasks || []).filter((task) => CHECKLIST_TASK_IDS.has(task.id))
  ), [selected]);

  const visibleTimeline = useMemo(() => (
    (selected?.timeline || []).filter((event) => VISIBLE_TIMELINE_TYPES.has(event.type))
  ), [selected]);

  const displayCases = useMemo(() => {
    const sourceCases = cases.filter((item) => getCaseSourceKey(item) === source);
    if (!selected) return sourceCases;
    if (sourceCases.some((item) => item.id === selected.id)) return sourceCases;
    if (!isCaseInStatusFilter(selected, status)) return sourceCases;
    if (getCaseSourceKey(selected) !== source) return sourceCases;
    return [selected, ...sourceCases];
  }, [cases, selected, source, status]);
  const isListRefreshing = loading && displayCases.length > 0;

  const findRelatedEmailForTimeline = useCallback((event) => {
    const metadata = event?.metadata || {};
    const emails = selected?.emails || [];
    if (metadata.resend_id) {
      return emails.find((mail) => mail.resend_id === metadata.resend_id) || null;
    }
    if (metadata.subject) {
      return emails.find((mail) => mail.subject === metadata.subject) || null;
    }
    return null;
  }, [selected]);

  const findRelatedNoteForTimeline = useCallback((event) => {
    const noteId = event?.metadata?.note_id;
    if (!noteId) return null;
    return (selected?.notes || []).find((note) => note.id === noteId) || null;
  }, [selected]);

  const showScopedError = useCallback((scope, fallback, err) => {
    const message = err?.message || fallback;
    setActionError((prev) => ({ ...prev, [scope]: message }));
    showToast(message, 'error', 6000);
  }, [showToast]);

  const clearScopedError = (scope) => {
    setActionError((prev) => {
      if (!prev[scope]) return prev;
      const next = { ...prev };
      delete next[scope];
      return next;
    });
  };

  const createStaleCaseError = () => {
    const staleError = new Error('다른 업무 케이스로 이동해 작업을 중단했어요.');
    staleError.isStaleAdminOpsCase = true;
    return staleError;
  };

  const assertCaseStillSelected = (caseId) => {
    if (selectedRef.current?.id !== caseId) {
      throw createStaleCaseError();
    }
  };

  const hydrateRecipientForm = useCallback((caseData) => {
    const nextForm = getRecipientFormFromCase(caseData);
    recipientFormRef.current = nextForm;
    recipientDirtyRef.current = false;
    recipientCaseIdRef.current = caseData?.id || '';
    recipientDraftVersionRef.current += 1;
    setRecipientForm(nextForm);
    setRecipientTotalTouched(!isPolicySuggestedTotal(nextForm));
    setRecipientFormDirty(false);
    setRecipientSaveState('saved');
  }, []);

  const loadCases = useCallback(async ({ ignoreCaseParam = false } = {}) => {
    if (!user) return;
    const requestId = listRequestIdRef.current + 1;
    listRequestIdRef.current = requestId;
    const isStale = () => listRequestIdRef.current !== requestId;
    setLoading(true);
    setError('');
    try {
      const res = await getAdminOperationCases({ status, query, limit: 80, source });
      if (isStale()) return;
      const items = res.items || [];
      setCases(items);
      setCounts(res.counts || {});
      setSourceCounts(res.source_counts || {});
      const urlCase = searchParams.get('case');
      const caseFromUrl = ignoreCaseParam || urlCase === ignoredUrlCaseRef.current ? '' : urlCase;
      const currentSelected = selectedRef.current;
      if (currentSelected) {
        const listItem = items.find((item) => item.id === currentSelected.id);
        if (listItem) {
          setSelected((prev) => (prev?.id === listItem.id ? {
            ...prev,
            ...listItem,
            tasks: prev.tasks,
            notes: prev.notes,
            timeline: prev.timeline,
            documents: prev.documents,
            emails: prev.emails,
          } : prev));
        }
      }
      if (!currentSelected && (items.length > 0 || caseFromUrl)) {
        const targetId = caseFromUrl || items[0].id;
        setDetailLoading(true);
        const detail = await getAdminOperationCase(targetId);
        if (isStale()) return;
        setSelected(detail);
        if (caseFromUrl && getCaseSourceKey(detail) !== source) {
          setSource(getCaseSourceKey(detail));
        }
      }
    } catch (err) {
      if (isStale()) return;
      setError(err.message || '업무 목록을 불러오지 못했어요.');
    } finally {
      if (!isStale()) {
        setLoading(false);
        setDetailLoading(false);
      }
    }
  }, [query, searchParams, source, status, user]);

  // 목록에 없는 업무가 상세에 남아 있으면 다른 학교로 서류·쿠폰이 나갈 수 있어서, 탭을 직접 바꿀 때는 선택을 비우고 새 목록 첫 건을 다시 고른다.
  const changeListFilter = ({ source: nextSource = source, status: nextStatus = status }) => {
    if (nextSource === source && nextStatus === status) return;
    const current = selectedRef.current;
    if (current && (getCaseSourceKey(current) !== nextSource || !isCaseInStatusFilter(current, nextStatus))) {
      ignoredUrlCaseRef.current = searchParams.get('case') || '';
      detailRequestIdRef.current += 1;
      selectedRef.current = null;
      setSelected(null);
      setDocumentResult(null);
      setLoadingCaseId('');
    }
    setSource(nextSource);
    setStatus(nextStatus);
  };

  const loadDetail = async (caseId) => {
    const requestId = detailRequestIdRef.current + 1;
    detailRequestIdRef.current = requestId;
    setLoadingCaseId(caseId);
    setDetailLoading(true);
    setError('');
    try {
      const detail = await getAdminOperationCase(caseId);
      if (detailRequestIdRef.current !== requestId) return;
      setSelected(detail);
      setDocumentResult(null);
    } catch (err) {
      if (detailRequestIdRef.current !== requestId) return;
      setError(err.message || '업무 케이스를 불러오지 못했어요.');
    } finally {
      if (detailRequestIdRef.current === requestId) {
        setDetailLoading(false);
        setLoadingCaseId('');
      }
    }
  };

  const refreshSelectedDetail = useCallback(async ({ silent = true } = {}) => {
    const currentSelected = selectedRef.current;
    if (!user || !currentSelected?.id) return;
    const targetCaseId = currentSelected.id;
    const requestMarker = detailRequestIdRef.current;
    if (!silent) setDetailLoading(true);
    try {
      const detail = await getAdminOperationCase(targetCaseId);
      if (detailRequestIdRef.current !== requestMarker || selectedRef.current?.id !== targetCaseId) return;
      setSelected(detail);
      setCases((prev) => prev.map((item) => (item.id === detail.id ? { ...item, ...detail } : item)));
    } catch (err) {
      if (!silent && selectedRef.current?.id === targetCaseId) {
        setError(err.message || '업무 케이스를 새로고침하지 못했어요.');
      }
    } finally {
      if (!silent && detailRequestIdRef.current === requestMarker && selectedRef.current?.id === targetCaseId) {
        setDetailLoading(false);
      }
    }
  }, [user]);

  useEffect(() => {
    if (!selected) return;
    const selectedCaseId = selected.id || '';
    const isDifferentCase = recipientCaseIdRef.current !== selectedCaseId;
    if (isDifferentCase || !recipientDirtyRef.current) {
      hydrateRecipientForm(selected);
    }
    if (isDifferentCase) {
      setCouponForm(getDefaultCouponFormFromCase(selected));
      setDocsEmailForm({ subject: '', body: '' });
      setSelectedDocumentTypes(PRE_PURCHASE_DOCUMENT_TYPES);
      setDocumentResult(null);
    }
  }, [hydrateRecipientForm, selected]);

  useEffect(() => {
    recipientFormRef.current = recipientForm;
  }, [recipientForm]);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/auth');
      return;
    }
    if (user) loadCases();
  }, [authLoading, loadCases, navigate, user]);

  useEffect(() => {
    if (!user || !selected?.id) return undefined;
    const refreshIfVisible = () => {
      if (document.visibilityState === 'visible') {
        refreshSelectedDetail({ silent: true });
      }
    };
    window.addEventListener('focus', refreshIfVisible);
    document.addEventListener('visibilitychange', refreshIfVisible);
    const timer = window.setInterval(refreshIfVisible, 20000);
    return () => {
      window.removeEventListener('focus', refreshIfVisible);
      document.removeEventListener('visibilitychange', refreshIfVisible);
      window.clearInterval(timer);
    };
  }, [refreshSelectedDetail, selected?.id, user]);

  const handleFormChange = (key, value) => {
    if (key === 'total_amount') {
      setFormTotalTouched(Number(value || 0) !== getSuggestedTotal(form.duration_months, form.quantity));
    }
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'duration_months') {
        if (formTotalTouched) {
          next.unit_price = getDerivedUnitPrice(next);
        } else {
          next.unit_price = getPolicyUnitPrice(value, next.quantity);
          next.total_amount = getSuggestedTotal(value, next.quantity);
        }
      }
      if (key === 'quantity') {
        if (formTotalTouched) {
          next.unit_price = getDerivedUnitPrice(next);
        } else {
          next.unit_price = getPolicyUnitPrice(next.duration_months, value);
          next.total_amount = getSuggestedTotal(next.duration_months, value);
        }
      }
      if (key === 'total_amount') {
        next.unit_price = Math.round(Number(value || 0) / Math.max(1, Number(next.quantity || 1)));
      }
      return next;
    });
  };

  const handleRecipientChange = (key, value) => {
    recipientDirtyRef.current = true;
    recipientDraftVersionRef.current += 1;
    if (key === 'total_amount') {
      setRecipientTotalTouched(Number(value || 0) !== getSuggestedTotal(recipientForm.duration_months, recipientForm.quantity));
    }
    setRecipientFormDirty(true);
    setRecipientSaveState('dirty');
    clearScopedError('recipient');
    setDocumentResult(null);
    setRecipientForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'duration_months') {
        if (recipientTotalTouched) {
          next.unit_price = getDerivedUnitPrice(next);
        } else {
          next.unit_price = getPolicyUnitPrice(value, next.quantity);
          next.total_amount = getSuggestedTotal(value, next.quantity);
        }
      }
      if (key === 'quantity') {
        if (recipientTotalTouched) {
          next.unit_price = getDerivedUnitPrice(next);
        } else {
          next.unit_price = getPolicyUnitPrice(next.duration_months, value);
          next.total_amount = getSuggestedTotal(next.duration_months, value);
        }
      }
      if (key === 'total_amount') {
        next.unit_price = Math.round(Number(value || 0) / Math.max(1, Number(next.quantity || 1)));
      }
      recipientFormRef.current = next;
      return next;
    });
  };

  const applyFormSuggestedTotal = () => {
    setFormTotalTouched(false);
    setForm((prev) => {
      const next = {
        ...prev,
        unit_price: getPolicyUnitPrice(prev.duration_months, prev.quantity),
        total_amount: getSuggestedTotal(prev.duration_months, prev.quantity),
      };
      return next;
    });
  };

  const applyRecipientSuggestedTotal = () => {
    recipientDirtyRef.current = true;
    recipientDraftVersionRef.current += 1;
    setRecipientTotalTouched(false);
    setRecipientFormDirty(true);
    setRecipientSaveState('dirty');
    clearScopedError('recipient');
    setDocumentResult(null);
    setRecipientForm((prev) => {
      const next = {
        ...prev,
        unit_price: getPolicyUnitPrice(prev.duration_months, prev.quantity),
        total_amount: getSuggestedTotal(prev.duration_months, prev.quantity),
      };
      recipientFormRef.current = next;
      return next;
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setBusyAction('create');
    setError('');
    try {
      const payload = {
        ...form,
        plan_id: getPlanIdFromMonths(form.duration_months),
        duration_months: normalizeDurationMonths(form.duration_months),
        quantity: Number(form.quantity || 1),
        unit_price: getDerivedUnitPrice(form),
        total_amount: getFormTotal(form),
        next_action: form.next_action || DEFAULT_NEXT_ACTION,
      };
      const created = await createAdminOperationCase(payload);
      setSelected(created);
      setShowCreate(false);
      setForm({ ...emptyForm, contract_date: getTodayDateString() });
      setFormTotalTouched(false);
      showToast('업무 케이스를 만들었어요.', 'success');
      const createdSource = getCaseSourceKey(created);
      if (createdSource !== source) {
        setSource(createdSource);
      } else {
        await loadCases();
      }
    } catch (err) {
      setError(err.message || '업무 케이스를 만들지 못했어요.');
    } finally {
      setBusyAction('');
    }
  };

  const addNote = async () => {
    if (!selected || !noteBody.trim()) return;
    setBusyAction('note');
    clearScopedError('memo');
    try {
      const note = await addAdminOperationNote(selected.id, noteBody.trim());
      setNoteBody('');
      setEditingNoteId('');
      setEditingNoteBody('');
      setSelected((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          memo_summary: note.body || prev.memo_summary,
          notes: [note, ...(prev.notes || [])],
        };
      });
      showToast('메모를 남겼어요.', 'success');
      loadCases();
    } catch (err) {
      showScopedError('memo', '메모를 저장하지 못했어요.', err);
    } finally {
      setBusyAction('');
    }
  };

  const toggleNote = async (note) => {
    if (!selected || !note?.id) return;
    const previousSelected = selected;
    const nextCompleted = !note.completed;
    const now = new Date().toISOString();
    setSelected((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        notes: (prev.notes || []).map((item) => (
          item.id === note.id
            ? {
              ...item,
              completed: nextCompleted,
              completed_at: nextCompleted ? now : null,
              completed_by_email: nextCompleted ? (user?.email || item.completed_by_email || '') : '',
            }
            : item
        )),
      };
    });
    setBusyAction(`note-${note.id}`);
    clearScopedError('memo');
    try {
      const updated = await updateAdminOperationNote(selected.id, note.id, { completed: nextCompleted });
      setSelected((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          notes: (prev.notes || []).map((item) => (item.id === note.id ? updated : item)),
        };
      });
      loadCases();
    } catch (err) {
      setSelected(previousSelected);
      showScopedError('memo', '메모 TODO 상태를 저장하지 못했어요.', err);
    } finally {
      setBusyAction('');
    }
  };

  const startEditNote = (note) => {
    setEditingNoteId(note.id);
    setEditingNoteBody(note.body || '');
  };

  const cancelEditNote = () => {
    setEditingNoteId('');
    setEditingNoteBody('');
  };

  const saveEditedNote = async (note) => {
    if (!selected || !note?.id) return;
    const body = editingNoteBody.trim();
    if (!body) {
      showScopedError('memo', '메모 내용을 입력해주세요.');
      return;
    }
    setBusyAction(`note-edit-${note.id}`);
    clearScopedError('memo');
    try {
      await updateAdminOperationNote(selected.id, note.id, { body });
      cancelEditNote();
      const detail = await getAdminOperationCase(selected.id);
      setSelected(detail);
      showToast('메모를 수정했어요.', 'success');
      loadCases();
    } catch (err) {
      showScopedError('memo', '메모를 수정하지 못했어요.', err);
    } finally {
      setBusyAction('');
    }
  };

  const deleteNote = async (note) => {
    if (!selected || !note?.id) return;
    if (!window.confirm('이 메모를 삭제할까요? 삭제한 메모는 되돌릴 수 없어요.')) {
      return;
    }
    setBusyAction(`note-delete-${note.id}`);
    clearScopedError('memo');
    try {
      await deleteAdminOperationNote(selected.id, note.id);
      if (editingNoteId === note.id) cancelEditNote();
      const detail = await getAdminOperationCase(selected.id);
      setSelected(detail);
      showToast('메모를 삭제했어요.', 'success');
      loadCases();
    } catch (err) {
      showScopedError('memo', '메모를 삭제하지 못했어요.', err);
    } finally {
      setBusyAction('');
    }
  };

  const handleTask = async (task) => {
    if (!selected) return;
    const previousSelected = selected;
    const nextCompleted = !task.completed;
    const now = new Date().toISOString();
    setSelected((prev) => {
      if (!prev) return prev;
      const nextStatus = task.id === 'tax_invoice_issued'
        ? (nextCompleted ? 'done' : 'in_progress')
        : prev.status;
      return {
        ...prev,
        status: nextStatus,
        effective_status: nextStatus,
        next_action: task.id === 'tax_invoice_issued' && nextCompleted
          ? '완료된 업무입니다.'
          : prev.next_action,
        tasks: (prev.tasks || []).map((item) => (
          item.id === task.id || (task.id === 'tax_invoice_issued' && nextCompleted)
            ? {
              ...item,
              completed: task.id === 'tax_invoice_issued' && nextCompleted ? true : nextCompleted,
              completed_at: (task.id === 'tax_invoice_issued' && nextCompleted) || nextCompleted ? (item.completed_at || now) : null,
              completed_by_email: (task.id === 'tax_invoice_issued' && nextCompleted) || nextCompleted ? (item.completed_by_email || user?.email || '') : '',
            }
            : item
        )),
      };
    });
    setBusyAction(`task-${task.id}`);
    clearScopedError('tasks');
    try {
      const updated = await updateAdminOperationTask(selected.id, task.id, nextCompleted);
      const shouldMoveToDoneTab = task.id === 'tax_invoice_issued'
        && nextCompleted
        && (updated.effective_status || updated.status) === 'done';
      const shouldMoveToInProgressTab = task.id === 'tax_invoice_issued'
        && !nextCompleted
        && (updated.effective_status || updated.status) === 'in_progress';
      if (shouldMoveToDoneTab) {
        if (status !== 'done') {
          setStatus('done');
        } else {
          loadCases();
        }
      } else if (shouldMoveToInProgressTab && status === 'done') {
        setStatus('in_progress');
      }
      setSelected(updated);
      if (!shouldMoveToDoneTab && !(shouldMoveToInProgressTab && status === 'done')) {
        loadCases();
      }
    } catch (err) {
      setSelected(previousSelected);
      showScopedError('tasks', '체크리스트를 저장하지 못했어요.', err);
    } finally {
      setBusyAction('');
    }
  };

  const buildRecipientPayload = (draft = recipientFormRef.current) => (
    getRecipientPayloadFromForm(draft)
  );

  const persistRecipientForAction = async ({ skipIfClean = false } = {}) => {
    const currentSelected = selectedRef.current;
    if (!currentSelected?.id) return null;
    if (skipIfClean && !recipientDirtyRef.current) return currentSelected;

    setRecipientSaveState('saving');
    const targetCaseId = currentSelected.id;
    const savingVersion = recipientDraftVersionRef.current;
    const payload = buildRecipientPayload();
    const isTargetStillSelected = () => selectedRef.current?.id === targetCaseId;
    try {
      const updated = await updateAdminOperationCase(targetCaseId, payload);
      if (!isTargetStillSelected()) {
        throw createStaleCaseError();
      }
      const nextForm = getRecipientFormFromCase(updated);
      recipientCaseIdRef.current = updated.id || targetCaseId;
      if (recipientDraftVersionRef.current === savingVersion) {
        recipientFormRef.current = nextForm;
        recipientDirtyRef.current = false;
        setRecipientForm(nextForm);
        setRecipientTotalTouched(!isPolicySuggestedTotal(nextForm));
        setRecipientFormDirty(false);
        setRecipientSaveState('saved');
      } else {
        recipientDirtyRef.current = true;
        setRecipientFormDirty(true);
        setRecipientSaveState('dirty');
      }
      setSelected(updated);
      return updated;
    } catch (err) {
      if (!isTargetStillSelected()) {
        throw createStaleCaseError();
      }
      recipientDirtyRef.current = true;
      setRecipientFormDirty(true);
      setRecipientSaveState('error');
      throw err;
    }
  };

  const saveRecipientDraftOnBlur = (event) => {
    const nextFocusedElement = event.relatedTarget;
    if (nextFocusedElement && event.currentTarget.contains(nextFocusedElement)) return;
    if (!recipientDirtyRef.current || recipientSaveState === 'saving') return;
    persistRecipientForAction({ skipIfClean: true }).catch((err) => {
      if (err?.isStaleAdminOpsCase) return;
      recipientDirtyRef.current = true;
      setRecipientFormDirty(true);
      setRecipientSaveState('error');
      showScopedError('recipient', '수신자 정보를 자동 저장하지 못했어요.', err);
    });
  };

  const previewDocuments = async () => {
    if (!selected) return;
    setBusyAction('docs-preview');
    clearScopedError('documents');
    try {
      const actionCaseId = selected.id;
      const updated = await persistRecipientForAction();
      assertCaseStillSelected(actionCaseId);
      const res = await previewAdminOperationDocuments(updated.id, selectedDocumentTypes);
      assertCaseStillSelected(actionCaseId);
      const generatedDocuments = res.documents || [];
      setDocumentResult(generatedDocuments);
      const detail = await getAdminOperationCase(updated.id);
      assertCaseStillSelected(actionCaseId);
      setSelected(detail);
      setDocsEmailForm(buildGeneratedDocsEmail(updated, generatedDocuments));
      showToast('문서를 만들었어요. 아래에서 바로 확인할 수 있어요.', 'success');
    } catch (err) {
      if (err?.isStaleAdminOpsCase) return;
      showScopedError('documents', '문서 패키지를 만들지 못했어요.', err);
    } finally {
      setBusyAction('');
    }
  };

  const sendDocs = async () => {
    if (!selected) return;
    const pendingPayload = buildRecipientPayload();
    const bccCount = pendingPayload.cc_emails.length;
    const confirmMessage = bccCount > 0
      ? `${pendingPayload.contact_email || '담당자 이메일'}로 서류 메일을 실제 발송할까요?\n숨은참조 ${bccCount}개도 함께 발송됩니다.`
      : `${pendingPayload.contact_email || '담당자 이메일'}로 서류 메일을 실제 발송할까요?`;
    const stageWarning = getDocsStageWarning(selectedDocumentTypes, docsEmailForm);
    if (!window.confirm(stageWarning ? `${confirmMessage}\n\n주의: ${stageWarning}` : confirmMessage)) {
      return;
    }
    setBusyAction('docs-send');
    clearScopedError('documents');
    try {
      const actionCaseId = selected.id;
      const updated = await persistRecipientForAction();
      assertCaseStillSelected(actionCaseId);
      const res = await sendAdminOperationDocumentsEmail(updated.id, {
        dryRun: false,
        documentTypes: selectedDocumentTypes,
        subject: docsEmailForm.subject,
        body: docsEmailForm.body,
      });
      assertCaseStillSelected(actionCaseId);
      const linkNote = res.payment_link?.url ? ' 카드 결제 링크도 함께 넣었어요.' : '';
      showToast(`서류 메일을 발송했어요.${linkNote} Resend ID: ${res.resend_id || '-'}`, 'success', 6000);
      const detail = await getAdminOperationCase(updated.id);
      assertCaseStillSelected(actionCaseId);
      setSelected(detail);
      loadCases();
    } catch (err) {
      if (err?.isStaleAdminOpsCase) return;
      showScopedError('documents', '서류 메일을 발송하지 못했어요.', err);
    } finally {
      setBusyAction('');
    }
  };

  const issueCoupons = async (dryRun = true) => {
    if (!selected) return;
    if (!dryRun && !window.confirm(`${selected.organization || '이 케이스'}에 쿠폰을 실제 발급할까요? 쿠폰 코드는 생성 후 되돌리기 어렵습니다.`)) {
      return;
    }
    setBusyAction('coupon');
    clearScopedError('coupon');
    try {
      const actionCaseId = selected.id;
      const updated = await persistRecipientForAction();
      assertCaseStillSelected(actionCaseId);
      const res = await issueAdminOperationCoupons(updated.id, {
        ...couponForm,
        count: couponForm.count || updated.quantity,
        benefitMonths: couponForm.benefitMonths || updated.duration_months || 12,
        dryRun,
      });
      assertCaseStillSelected(actionCaseId);
      showToast(dryRun ? `${res.count}개 쿠폰 발급 전 확인이 완료됐어요.` : `${res.count}개 쿠폰을 실제 발급했어요.`, 'success');
      const detail = await getAdminOperationCase(updated.id);
      assertCaseStillSelected(actionCaseId);
      setSelected(detail);
      loadCases();
    } catch (err) {
      if (err?.isStaleAdminOpsCase) return;
      showScopedError('coupon', '쿠폰 발급을 처리하지 못했어요.', err);
    } finally {
      setBusyAction('');
    }
  };

  const resendCouponEmail = async () => {
    if (!selected) return;
    if (!selected.coupon_codes?.length) {
      showScopedError('coupon', '재발송할 쿠폰이 없습니다. 먼저 쿠폰을 발급해주세요.');
      return;
    }
    const pendingPayload = buildRecipientPayload();
    const bccCount = pendingPayload.cc_emails.length;
    const confirmMessage = bccCount > 0
      ? `${pendingPayload.contact_email || '담당자 이메일'}로 기존 쿠폰 안내 메일을 다시 보낼까요?\n숨은참조 ${bccCount}개도 함께 발송됩니다.`
      : `${pendingPayload.contact_email || '담당자 이메일'}로 기존 쿠폰 안내 메일을 다시 보낼까요?`;
    if (!window.confirm(confirmMessage)) {
      return;
    }
    setBusyAction('coupon-resend');
    clearScopedError('coupon');
    try {
      const actionCaseId = selected.id;
      const updated = await persistRecipientForAction();
      assertCaseStillSelected(actionCaseId);
      const res = await resendAdminOperationCouponEmail(updated.id);
      assertCaseStillSelected(actionCaseId);
      showToast(`쿠폰 안내 메일을 재발송했어요. Resend ID: ${res.email?.resend_id || '-'}`, 'success', 6000);
      const detail = await getAdminOperationCase(updated.id);
      assertCaseStillSelected(actionCaseId);
      setSelected(detail);
      loadCases();
    } catch (err) {
      if (err?.isStaleAdminOpsCase) return;
      showScopedError('coupon', '쿠폰 안내 메일을 재발송하지 못했어요.', err);
    } finally {
      setBusyAction('');
    }
  };

  const preparePaymentLink = async () => {
    if (!selected) return;
    setBusyAction('paylink');
    clearScopedError('paylink');
    try {
      const actionCaseId = selected.id;
      const updated = await persistRecipientForAction();
      assertCaseStillSelected(actionCaseId);
      const res = await ensureAdminOperationPaymentLink(updated.id);
      assertCaseStillSelected(actionCaseId);
      setSelected(res.case);
      const copied = await copyText(res.payment_link.url);
      showToast(copied ? '카드 결제 링크를 복사했어요.' : '카드 결제 링크를 만들었어요.', 'success');
    } catch (err) {
      if (err?.isStaleAdminOpsCase) return;
      showScopedError('paylink', '카드 결제 링크를 만들지 못했어요.', err);
    } finally {
      setBusyAction('');
    }
  };

  const sendPaymentLinkEmail = async () => {
    if (!selected) return;
    const pendingPayload = buildRecipientPayload();
    if (!window.confirm(`${pendingPayload.contact_email || '담당자 이메일'}로 카드 결제 링크 메일을 보낼까요?\n금액: ${fmtMoney(getFormTotal(pendingPayload))}`)) {
      return;
    }
    setBusyAction('paylink-send');
    clearScopedError('paylink');
    try {
      const actionCaseId = selected.id;
      const updated = await persistRecipientForAction();
      assertCaseStillSelected(actionCaseId);
      await sendAdminOperationPaymentLinkEmail(updated.id, { dryRun: false });
      assertCaseStillSelected(actionCaseId);
      showToast('카드 결제 링크 메일을 보냈어요.', 'success');
      const detail = await getAdminOperationCase(updated.id);
      assertCaseStillSelected(actionCaseId);
      setSelected(detail);
      loadCases();
    } catch (err) {
      if (err?.isStaleAdminOpsCase) return;
      showScopedError('paylink', '결제 링크 메일을 보내지 못했어요.', err);
    } finally {
      setBusyAction('');
    }
  };

  const applyDocsTemplate = (mode) => {
    const template = buildDocsTemplate(selected, mode);
    setDocsEmailForm({ subject: template.subject, body: template.body });
    if (template.documentTypes) {
      setSelectedDocumentTypes(template.documentTypes);
    }
    showToast('메일 제목과 내용을 채웠어요.', 'success');
  };

  const toggleDocumentType = (docType) => {
    setSelectedDocumentTypes((prev) => {
      if (prev.includes(docType)) {
        const next = prev.filter((item) => item !== docType);
        return next.length > 0 ? next : prev;
      }
      return [...prev, docType];
    });
  };

  const saveRecipient = async () => {
    if (!selected) return;
    setBusyAction('recipient');
    clearScopedError('recipient');
    try {
      const actionCaseId = selected.id;
      const updated = await persistRecipientForAction();
      assertCaseStillSelected(actionCaseId);
      setSelected(updated);
      setDocumentResult(null);
      showToast('수신자와 견적 정보를 저장했어요.', 'success');
      loadCases();
    } catch (err) {
      if (err?.isStaleAdminOpsCase) return;
      recipientDirtyRef.current = true;
      setRecipientFormDirty(true);
      setRecipientSaveState('error');
      showScopedError('recipient', '수신자 정보를 저장하지 못했어요.', err);
    } finally {
      setBusyAction('');
    }
  };

  const deleteSelectedCase = async () => {
    if (!selected) return;
    if (!window.confirm(`${selected.organization || '이 업무'} 케이스를 삭제할까요? 테스트로 만든 케이스만 삭제해주세요.`)) {
      return;
    }
    setBusyAction('delete');
    clearScopedError('case');
    try {
      await deleteAdminOperationCase(selected.id);
      selectedRef.current = null;
      setSelected(null);
      setDocumentResult(null);
      navigate('/admin/operations', { replace: true });
      showToast('업무 케이스를 삭제했어요.', 'success');
      await loadCases({ ignoreCaseParam: true });
    } catch (err) {
      showScopedError('case', '업무 케이스를 삭제하지 못했어요.', err);
    } finally {
      setBusyAction('');
    }
  };

  if (authLoading) return <div className="admin-ops__loading">불러오는 중...</div>;

  return (
    <div className="admin-ops">
      <Helmet>
        <title>업무 자동화 | 왓퀴즈 어드민</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="admin-ops__header">
        <button className="admin-ops__back" onClick={() => navigate('/admin')}>관리자 홈</button>
        <div>
          <p className="admin-ops__eyebrow">Admin Ops Cockpit</p>
          <h1>학교·기관 업무 자동화</h1>
          <p>요청, 서류 발송, 쿠폰, 통화 메모를 한 케이스에서 확인해요.</p>
        </div>
        <div className="admin-ops__header-actions">
          <button
            className="admin-ops__header-button admin-ops__header-button--primary"
            onClick={() => setShowCreate((v) => !v)}
            type="button"
          >
            <span>새 업무 만들기</span>
          </button>
          <button
            className="admin-ops__header-button admin-ops__header-button--secondary"
            onClick={() => window.open('https://www.s2b.kr/S2BNVendor/S2B/srcweb/main.jsp', '_blank', 'noopener,noreferrer')}
            type="button"
          >
            <span>S2B 바로가기</span>
          </button>
          <button
            className="admin-ops__header-button admin-ops__header-button--secondary"
            onClick={() => setShowDocsPanel((v) => !v)}
            type="button"
            aria-expanded={showDocsPanel}
          >
            <span>행정 서류 관리</span>
          </button>
        </div>
      </div>

      {showDocsPanel && <AdminManagedDocumentsPanel />}

      {error && <div className="admin-ops__alert admin-ops__alert--error">{error}</div>}

      {showCreate && (
        <form className="admin-ops__create" onSubmit={handleCreate}>
          <div className="admin-ops__form-grid">
            <label>학교/기관명<input value={form.organization} onChange={(e) => handleFormChange('organization', e.target.value)} required /></label>
            <label>담당자<input value={form.contact_name} onChange={(e) => handleFormChange('contact_name', e.target.value)} /></label>
            <label>이메일<input type="email" value={form.contact_email} onChange={(e) => handleFormChange('contact_email', e.target.value)} /></label>
            <label>전화번호<input value={form.contact_phone} onChange={(e) => handleFormChange('contact_phone', e.target.value)} /></label>
            <label>계약일자<input type="date" value={form.contract_date} onChange={(e) => handleFormChange('contract_date', e.target.value)} /></label>
            <label>
              사용 기간(개월)
              <input type="number" min="1" max="24" step="1" value={form.duration_months} onChange={(e) => handleFormChange('duration_months', e.target.value)} placeholder="예: 2" required />
              <small className="admin-ops__field-hint">2개월, 4개월처럼 직접 입력할 수 있어요.</small>
            </label>
            <label>수량<input type="number" min="1" max="999" value={form.quantity} onChange={(e) => handleFormChange('quantity', e.target.value)} /></label>
            <label>총액<input type="number" min="0" value={form.total_amount} onChange={(e) => handleFormChange('total_amount', e.target.value)} /></label>
            <label>자동 계산 단가<input type="number" value={formPricing.derivedUnitPrice} readOnly /></label>
          </div>
          <div className="admin-ops__price-card">
            <strong>현재 단체 구매 가격 참고</strong>
            <span>정가: 1개월 {fmtMoney(GROUP_PLAN_PRICES['1month'].price)} · 3개월 {fmtMoney(GROUP_PLAN_PRICES['3month'].price)} · 6개월 {fmtMoney(GROUP_PLAN_PRICES['6month'].price)} · 12개월 {fmtMoney(GROUP_PLAN_PRICES['12month'].price)}</span>
            <span>선택 조건 기준 {formPricing.discountLabel} · 추천 단가 {fmtMoney(formPricing.suggestedUnitPrice)} · 추천 총액 {fmtMoney(formPricing.suggestedTotal)} · 입력 총액 {fmtMoney(formPricing.enteredTotal)}</span>
            <span>12개월 단가 예시: {groupPricingRows.map((row) => `${row.label} ${fmtMoney(row.prices['12month'])}`).join(' · ')} · <a href="/admin/pricing-policy" target="_blank" rel="noopener noreferrer">전체 정책표 보기</a></span>
            {formTotalTouched && formPricing.enteredTotal !== formPricing.suggestedTotal && (
              <button type="button" className="admin-ops__secondary" onClick={applyFormSuggestedTotal}>
                추천가 적용
              </button>
            )}
          </div>
          <label className="admin-ops__wide-label">메모<textarea value={form.memo_summary} onChange={(e) => handleFormChange('memo_summary', e.target.value)} placeholder="예: 1달 단위로 쪼개서 견적 필요, 행정실 S2B 담당자 연결 필요" /></label>
          <label className="admin-ops__wide-label">다음 할 일<input value={form.next_action} onChange={(e) => handleFormChange('next_action', e.target.value)} placeholder="예: 견적서만 먼저 보내기" /></label>
          <div className="admin-ops__form-actions">
            <button type="button" className="admin-ops__secondary" onClick={() => setShowCreate(false)}>닫기</button>
            <button type="submit" className="admin-ops__primary admin-ops__button-spinner" disabled={busyAction === 'create'}>{busyAction === 'create' ? '만드는 중...' : '업무 만들기'}</button>
          </div>
        </form>
      )}

      <div className="admin-ops__layout">
        <aside className="admin-ops__list-panel">
          <div className="admin-ops__tabs">
            {STATUS_OPTIONS.map((item) => (
              <button key={item.key} className={status === item.key ? 'is-active' : ''} onClick={() => changeListFilter({ status: item.key })}>
                {item.label}
                {item.key !== 'all' && <span>{counts[item.key] || 0}</span>}
              </button>
            ))}
          </div>
          <div className="admin-ops__source-filter" role="group" aria-label="케이스 출처">
            {SOURCE_OPTIONS.map((item) => (
              <button
                key={item.key}
                type="button"
                className={source === item.key ? 'is-active' : ''}
                aria-pressed={source === item.key}
                onClick={() => changeListFilter({ source: item.key })}
              >
                {item.label}
                <span>{sourceCounts[item.key] || 0}</span>
              </button>
            ))}
          </div>
          <input className="admin-ops__search" value={queryInput} onChange={(e) => setQueryInput(e.target.value)} placeholder="학교명, 이메일, 메모 검색" />
          <button className="admin-ops__secondary admin-ops__full" onClick={loadCases} disabled={loading}>목록 새로고침</button>
          <div className="admin-ops__case-list" aria-label="업무 케이스 목록">
            {isListRefreshing && <div className="admin-ops__hint">목록을 최신 상태로 갱신하고 있어요.</div>}
            {loading && !isListRefreshing ? (
              <CaseListSkeleton count={isListRefreshing ? Math.max(3, Math.min(displayCases.length, 6)) : 3} />
            ) : displayCases.map((item) => (
              loadingCaseId === item.id ? (
                <CaseCardSkeleton key={item.id} />
              ) : (
                <div key={item.id} className={`admin-ops__case-card ${selected?.id === item.id ? 'is-selected' : ''}`}>
                  <button type="button" className="admin-ops__case-main" onClick={() => loadDetail(item.id)}>
                    {(() => {
                      const noteStats = getCaseTodoStats(item);
                      return (
                        <div className="admin-ops__case-status-row">
                          <span className={`admin-ops__badge ${noteStats.isComplete ? 'admin-ops__badge--done' : 'admin-ops__badge--todo-open'}`}>
                            {noteStats.isComplete ? '완료' : '미완료'}
                          </span>
                          <span className="admin-ops__todo-count">{noteStats.done}/{noteStats.total}</span>
                          {item.source === 'site_quote' && (
                            <span className="admin-ops__source-badge">사이트 서류</span>
                          )}
                        </div>
                      );
                    })()}
                    <strong>{item.organization || '(기관명 없음)'}</strong>
                    <small>{item.contact_name || '담당자 미입력'} · {item.contact_email || '이메일 없음'}</small>
                    <small>계약일 {formatContractDate(item.contract_date)}</small>
                  </button>
                  {selected?.id === item.id && (
                    <div className="admin-ops__case-timeline">
                      <b>최근 처리 흐름</b>
                      {visibleTimeline.map((event) => (
                        <div key={event.id} className="admin-ops__case-timeline-row">
                          <span>{event.message}<small>{formatDate(getTimelineTime(event))}</small></span>
                          <button
                            type="button"
                            onClick={() => setPreviewModal({
                              type: 'timeline',
                              title: event.message,
                              event,
                              relatedEmail: findRelatedEmailForTimeline(event),
                              relatedNote: findRelatedNoteForTimeline(event),
                            })}
                          >
                            자세히 보기
                          </button>
                        </div>
                      ))}
                      {visibleTimeline.length === 0 && <span>아직 보여줄 처리 기록이 없어요.</span>}
                    </div>
                  )}
                </div>
              )
            ))}
            {!loading && displayCases.length === 0 && <div className="admin-ops__empty">조건에 맞는 업무가 없어요.</div>}
          </div>
        </aside>

        <main className="admin-ops__detail-panel">
          {detailLoading && <DetailSkeleton />}
          {!selected && !detailLoading && <div className="admin-ops__empty admin-ops__empty--large">업무 케이스를 선택하거나 새로 만들어주세요.</div>}
          {selected && !detailLoading && (
            <>
              {isDetailBusy && (
                <div className="admin-ops__detail-loading" role="status" aria-live="polite">
                  {busyMessage}
                </div>
              )}
              <section className="admin-ops__detail-head">
                <div>
                  <span className={`admin-ops__badge admin-ops__badge--${selected.effective_status || selected.status}`}>
                    {STATUS_LABELS[selected.effective_status || selected.status] || selected.status}
                  </span>
                  <h2>{selected.organization || '(기관명 없음)'}</h2>
                  <p>{selected.contact_name || '담당자 미입력'} · {selected.contact_email || '이메일 없음'} · {selected.contact_phone || '전화번호 없음'}</p>
                </div>
                <div className="admin-ops__head-actions">
                  <button className="admin-ops__secondary" onClick={deleteSelectedCase} disabled={busyAction === 'delete'}>
                    테스트 케이스 삭제
                  </button>
                </div>
              </section>

              {(
                <>
                  <section className="admin-ops__summary-grid">
                    <div><span>상품</span><strong>{getProductLabel(selected.duration_months)}</strong></div>
                    <div><span>수량</span><strong>{selected.quantity || 1}개</strong></div>
                    <div><span>총액</span><strong>{fmtMoney(selectedTotal)}</strong></div>
                    <div><span>계약일자</span><strong>{formatContractDate(selected.contract_date)}</strong></div>
                  </section>

                  <section className="admin-ops__section" onBlur={saveRecipientDraftOnBlur}>
                    <div className="admin-ops__section-title admin-ops__section-title--with-status">
                      <div>
                        <h3>수신자와 문서 정보</h3>
                        <p>수정한 내용은 저장 후 문서와 쿠폰 안내 메일에 반영됩니다.</p>
                      </div>
                      <span className={`admin-ops__save-pill admin-ops__save-pill--${recipientFormDirty ? 'dirty' : recipientSaveState}`}>
                        {recipientSaveCopy}
                      </span>
                    </div>
                    {busyAction === 'recipient' && <SectionLoading message="수신자 정보를 저장하고 있어요" />}
                    {actionError.recipient && <div className="admin-ops__section-error">{actionError.recipient}</div>}
                    <div className="admin-ops__coupon-grid">
                      <label>담당자<input value={recipientForm.contact_name} onChange={(e) => handleRecipientChange('contact_name', e.target.value)} /></label>
                      <label>
                        <span className="admin-ops__label-row">
                          받는 이메일
                          <RecipientEmailHelp />
                        </span>
                        <input type="email" value={recipientForm.contact_email} onChange={(e) => handleRecipientChange('contact_email', e.target.value)} />
                      </label>
                      <label>
                        숨은참조 이메일
                        <textarea value={recipientForm.cc_emails} onChange={(e) => handleRecipientChange('cc_emails', e.target.value)} placeholder="예: ops@whatquiz.kr, manager@school.kr" />
                        <small className="admin-ops__field-hint">엔터, 쉼표, 세미콜론으로 여러 명을 입력할 수 있어요. 다른 영역으로 이동하면 자동 저장됩니다.</small>
                      </label>
                      <label>전화번호<input value={recipientForm.contact_phone} onChange={(e) => handleRecipientChange('contact_phone', e.target.value)} /></label>
                      <label>계약일자<input type="date" value={recipientForm.contract_date} onChange={(e) => handleRecipientChange('contract_date', e.target.value)} /></label>
                      <label>
                        사용 기간(개월)
                        <input type="number" min="1" max="24" step="1" value={recipientForm.duration_months} onChange={(e) => handleRecipientChange('duration_months', e.target.value)} placeholder="예: 4" required />
                        <small className="admin-ops__field-hint">표준 기간이 아니면 1개월 정가×개월에 인원 할인을 적용해요.</small>
                      </label>
                      <label>수량<input type="number" min="1" max="999" value={recipientForm.quantity} onChange={(e) => handleRecipientChange('quantity', e.target.value)} /></label>
                      <label>총액<input type="number" min="0" value={recipientForm.total_amount} onChange={(e) => handleRecipientChange('total_amount', e.target.value)} placeholder="문서에 찍힐 최종 금액" /></label>
                      <label>자동 계산 단가<input type="number" value={recipientPricing.derivedUnitPrice} readOnly /></label>
                      <label>분할/특이 요청<input value={recipientForm.split_policy} onChange={(e) => handleRecipientChange('split_policy', e.target.value)} placeholder="예: 1개월씩 쪼개서 발송" /></label>
                      <label className="admin-ops__wide-label">다음 할 일<input value={recipientForm.next_action} onChange={(e) => handleRecipientChange('next_action', e.target.value)} placeholder="예: 견적서만 먼저 보내기" /></label>
                    </div>
                    <div className="admin-ops__price-card">
                      <strong>현재 단체 구매 가격 참고</strong>
                      <span>정가: 1개월 {fmtMoney(GROUP_PLAN_PRICES['1month'].price)} · 3개월 {fmtMoney(GROUP_PLAN_PRICES['3month'].price)} · 6개월 {fmtMoney(GROUP_PLAN_PRICES['6month'].price)} · 12개월 {fmtMoney(GROUP_PLAN_PRICES['12month'].price)}</span>
                      <span>선택 조건 기준 {recipientPricing.discountLabel} · 추천 단가 {fmtMoney(recipientPricing.suggestedUnitPrice)} · 추천 총액 {fmtMoney(recipientPricing.suggestedTotal)} · 저장될 총액 {fmtMoney(recipientPricing.enteredTotal)}</span>
                      <span>12개월 단가 예시: {groupPricingRows.map((row) => `${row.label} ${fmtMoney(row.prices['12month'])}`).join(' · ')} · <a href="/admin/pricing-policy" target="_blank" rel="noopener noreferrer">전체 정책표 보기</a></span>
                      {recipientTotalTouched && recipientPricing.enteredTotal !== recipientPricing.suggestedTotal && (
                        <button type="button" className="admin-ops__secondary" onClick={applyRecipientSuggestedTotal}>
                          추천가 적용
                        </button>
                      )}
                    </div>
                    <div className="admin-ops__resend-box">
                      <strong>메일 발송 정보</strong>
                      <span>추가 수신자는 모두 숨은참조(BCC)로 발송되어 받는 사람에게 보이지 않습니다.</span>
                    </div>
                    <button className="admin-ops__primary admin-ops__button-spinner" onClick={saveRecipient} disabled={busyAction === 'recipient' || recipientSaveState === 'saving'}>{busyAction === 'recipient' || recipientSaveState === 'saving' ? '저장 중...' : (recipientFormDirty ? '지금 저장' : '수신자 정보 저장')}</button>
                  </section>

                  <section className="admin-ops__section">
                    <div className="admin-ops__section-title">
                      <h3>메모와 처리 체크리스트</h3>
                      <p>전화나 메일 요청은 메모로 남기고, 처리 상태는 아래 체크리스트로 추적해요. 메모는 서류 PDF에 들어가지 않아요.</p>
                    </div>
                    {(busyAction === 'note' || busyAction.startsWith('note-') || busyAction.startsWith('task-')) && (
                      <SectionLoading message={busyMessage} />
                    )}
                    {actionError.memo && <div className="admin-ops__section-error">{actionError.memo}</div>}
                    <textarea className="admin-ops__memo-input" value={noteBody} onChange={(e) => setNoteBody(e.target.value)} placeholder="예: 6/9 전화 요청 - 견적서 7개와 3개로 나눠 발송, 행정실에는 분할 사유 언급하지 않기" />
                    <button className="admin-ops__primary" onClick={addNote} disabled={busyAction === 'note' || !noteBody.trim()}>{busyAction === 'note' ? '저장 중...' : '메모 저장'}</button>
                    <div className="admin-ops__notes">
                      {(selected.notes || []).map((note) => (
                        <div
                          key={note.id}
                          className={`admin-ops__note ${note.completed ? 'is-done' : ''} ${editingNoteId === note.id ? 'is-editing' : ''}`}
                          onClick={() => {
                            if (editingNoteId !== note.id) toggleNote(note);
                          }}
                        >
                          <div className="admin-ops__note-head">
                            <button
                              type="button"
                              className="admin-ops__note-status"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleNote(note);
                              }}
                              disabled={busyAction === `note-${note.id}`}
                            >
                              {busyAction === `note-${note.id}` ? '저장 중' : (note.completed ? '완료' : '미완료')}
                            </button>
                            <div className="admin-ops__note-actions">
                              {editingNoteId === note.id ? (
                                <>
                                  <button type="button" onClick={(e) => {
                                    e.stopPropagation();
                                    saveEditedNote(note);
                                  }} disabled={busyAction === `note-edit-${note.id}` || !editingNoteBody.trim()}>
                                    {busyAction === `note-edit-${note.id}` ? '저장 중' : '수정 저장'}
                                  </button>
                                  <button type="button" onClick={(e) => {
                                    e.stopPropagation();
                                    cancelEditNote();
                                  }} disabled={busyAction === `note-edit-${note.id}`}>취소</button>
                                </>
                              ) : (
                                <>
                                  <button type="button" onClick={(e) => {
                                    e.stopPropagation();
                                    startEditNote(note);
                                  }} disabled={busyAction.startsWith('note-')}>수정</button>
                                  <button type="button" className="admin-ops__note-delete" onClick={(e) => {
                                    e.stopPropagation();
                                    deleteNote(note);
                                  }} disabled={busyAction === `note-delete-${note.id}`}>
                                    {busyAction === `note-delete-${note.id}` ? '삭제 중' : '삭제'}
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                          {editingNoteId === note.id ? (
                            <textarea
                              className="admin-ops__note-edit"
                              value={editingNoteBody}
                              onChange={(e) => setEditingNoteBody(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          ) : (
                            <p>{note.body}</p>
                          )}
                          <small>
                            {note.author_email} · {formatDate(note.created_at)}
                            {note.updated_at && ` · 수정: ${formatDate(note.updated_at)} · ${note.updated_by_email || ''}`}
                            {note.completed_at && ` · 완료: ${formatDate(note.completed_at)} · ${note.completed_by_email || ''}`}
                          </small>
                        </div>
                      ))}
                    </div>
                    <div className="admin-ops__section-subtitle">
                      <h4>처리 체크리스트</h4>
                      <p>완료된 항목도 다시 누르면 대기로 바뀝니다. 세금계산서 발급을 완료하면 케이스도 자동 완료됩니다.</p>
                    </div>
                    {actionError.tasks && <div className="admin-ops__section-error">{actionError.tasks}</div>}
                    <div className="admin-ops__task-list">
                      {visibleTasks.map((task) => {
                        const isSaving = busyAction === `task-${task.id}`;
                        return (
                          <button
                            key={task.id}
                            className={`${task.completed ? 'is-done' : ''} ${isSaving ? 'is-saving' : ''}`.trim()}
                            onClick={() => handleTask(task)}
                            disabled={isSaving}
                          >
                            <span>{isSaving ? '저장 중' : (task.completed ? '완료' : '대기')}</span>
                            <strong>{task.label}</strong>
                            {task.completed_at && <small>{formatDate(task.completed_at)} · {task.completed_by_email}</small>}
                          </button>
                        );
                      })}
                    </div>
                  </section>

                  <section className="admin-ops__section">
                    <div className="admin-ops__section-title">
                      <h3>요청 정리 및 문서 발송</h3>
                      <p>전화로 받은 요청을 체크하고, 필요한 서류를 만든 뒤 메일로 보내요.</p>
                    </div>
                    {(busyAction === 'docs-preview' || busyAction === 'docs-send') && (
                      <SectionLoading message={busyMessage} />
                    )}
                    {actionError.documents && <div className="admin-ops__section-error">{actionError.documents}</div>}
                    <div className="admin-ops__template-row">
                      <button onClick={() => applyDocsTemplate('default')}>기본 서류 메일 채우기</button>
                      <button onClick={() => applyDocsTemplate('quotationOnly')}>견적서만 채우기</button>
                      <button onClick={() => applyDocsTemplate('delivery')}>납품서 요청 답변 채우기</button>
                      <button onClick={() => applyDocsTemplate('quoteChange')}>수정 견적서 메일 채우기</button>
                    </div>
                    <div className="admin-ops__document-options">
                      {DOCUMENT_OPTIONS.map((doc) => (
                        <label key={doc.key} className="admin-ops__switch">
                          <input
                            type="checkbox"
                            checked={selectedDocumentTypes.includes(doc.key)}
                            onChange={() => toggleDocumentType(doc.key)}
                          />
                          {doc.label}
                        </label>
                      ))}
                    </div>
                    <div className="admin-ops__request-brief">
                      <strong>요청 정리</strong>
                      <span>필요 서류: {getSelectedDocumentLabels(selectedDocumentTypes)}</span>
                      <span>특이 요청: {recipientForm.split_policy || selected.memo_summary || '메모에 남겨주세요'}</span>
                    </div>
                    <input
                      value={docsEmailForm.subject}
                      onChange={(e) => setDocsEmailForm((prev) => ({ ...prev, subject: e.target.value }))}
                      placeholder="서류 메일 제목"
                    />
                    <textarea
                      className="admin-ops__memo-input"
                      value={docsEmailForm.body}
                      onChange={(e) => setDocsEmailForm((prev) => ({ ...prev, body: e.target.value }))}
                      placeholder="서류 메일 내용을 입력하거나 위 버튼으로 자동 채우세요."
                    />
                    <SignaturePreview senderEmail={user?.email} />
                    <p className="admin-ops__hint">
                      {isDeliveryPackage(selectedDocumentTypes)
                        ? '납품 서류 메일이라 카드 결제 버튼과 견적서 다시 만들기 링크는 넣지 않아요.'
                        : '서류 메일에는 카드 결제 버튼(저장된 총액·수량 그대로)과 학교가 견적서를 직접 다시 만들 수 있는 링크가 자동으로 들어가요.'}
                    </p>
                    <div className="admin-ops__action-row">
                      <button className="admin-ops__primary admin-ops__button-spinner" onClick={previewDocuments} disabled={busyAction === 'docs-preview'}>
                        {busyAction === 'docs-preview' ? '문서 만드는 중...' : '문서 만들기'}
                      </button>
                      <button className="admin-ops__danger admin-ops__button-spinner" onClick={sendDocs} disabled={busyAction === 'docs-send'}>
                        {busyAction === 'docs-send' ? '메일 보내는 중...' : '실제 메일 보내기'}
                      </button>
                    </div>
                    {documentResult && (
                      <div className="admin-ops__generated-box">
                        <div className="admin-ops__generated-head">
                          <div>
                            <strong>생성된 문서 {documentResult.length}개</strong>
                            <p>아래 파일이 방금 만들어졌어요. 먼저 내려받아 확인한 뒤 실제 메일을 보내면 됩니다.</p>
                            {documentResult.some((doc) => String(doc.type || '').endsWith('_hwpx')) && (
                              <p>메일에는 한글 원본이 있는 서류(품의서·내부 기안문)를 한글 파일로만 첨부해요.</p>
                            )}
                          </div>
                          <button
                            className="admin-ops__primary"
                            onClick={() => downloadDocumentsZip(documentResult, selected.organization)}
                          >
                            전체 ZIP 내려받기
                          </button>
                        </div>
                        <div className="admin-ops__doc-list">
                          {documentResult.map((doc) => (
                            <button key={`${doc.type}-${doc.filename}`} onClick={() => setPreviewModal({ type: 'document', title: doc.label, doc })}>{doc.label} 미리보기<span>{doc.filename}</span></button>
                          ))}
                        </div>
                      </div>
                    )}
                  </section>

                  <section className="admin-ops__section">
                    <div className="admin-ops__section-title">
                      <h3>카드 결제 링크</h3>
                      <p>학교가 카드로 바로 결제하길 원할 때 보내요. 결제되면 쿠폰이 결제자 메일로 자동 발송돼요.</p>
                    </div>
                    {(busyAction === 'paylink' || busyAction === 'paylink-send') && <SectionLoading message={busyMessage} />}
                    {actionError.paylink && <div className="admin-ops__section-error">{actionError.paylink}</div>}
                    {(() => {
                      const linkState = getPaymentLinkState(selected);
                      if (linkState.kind === 'paid') {
                        const payment = selected.card_payment || {};
                        return (
                          <div className="admin-ops__paylink admin-ops__paylink--paid">
                            <strong>카드 결제 완료 · {fmtMoney(payment.total_amount || linkState.link.total_amount)}</strong>
                            <span>
                              {formatDate(payment.paid_at || linkState.link.paid_at)} · 쿠폰 {payment.coupon_count || '-'}개 자동 발송
                              {payment.coupon_prefix ? ` (${payment.coupon_prefix}-001~)` : ''}
                              {payment.payer_email ? ` · ${payment.payer_email}` : ''}
                            </span>
                            <small>쿠폰이 이미 나갔으니 아래에서 쿠폰을 또 발급하지 마세요.</small>
                          </div>
                        );
                      }
                      return (
                        <div className="admin-ops__paylink">
                          {linkState.kind === 'active' ? (
                            <>
                              <strong>{fmtMoney(linkState.link.total_amount)} · {linkState.link.quantity}개 · {linkState.link.duration_months}개월</strong>
                              <a href={linkState.link.url} target="_blank" rel="noopener noreferrer">{linkState.link.url}</a>
                              <small>{formatDate(linkState.link.expires_at)}까지 쓸 수 있어요. 한 번 결제되면 링크가 닫혀요.</small>
                            </>
                          ) : (
                            <span>
                              {linkState.kind === 'stale'
                                ? (linkState.termsChanged
                                  ? '총액·수량·기간이 바뀌었어요. 링크를 다시 만들면 이전 링크는 막혀요.'
                                  : '이전 링크가 만료됐어요. 다시 만들어 주세요.')
                                : `저장된 총액 ${fmtMoney(selectedTotal)}으로 결제 링크를 만들어요. 서류 메일을 보내면 자동으로 함께 들어가요.`}
                            </span>
                          )}
                        </div>
                      );
                    })()}
                    {getPaymentLinkState(selected).kind !== 'paid' && (
                      <div className="admin-ops__action-row">
                        <button className="admin-ops__secondary admin-ops__button-spinner" onClick={preparePaymentLink} disabled={busyAction === 'paylink'}>
                          {getPaymentLinkState(selected).kind === 'active' ? '링크 복사' : '링크 만들고 복사'}
                        </button>
                        <button className="admin-ops__danger admin-ops__button-spinner" onClick={sendPaymentLinkEmail} disabled={busyAction === 'paylink-send'}>
                          {busyAction === 'paylink-send' ? '보내는 중...' : '결제 링크만 메일로 보내기'}
                        </button>
                      </div>
                    )}
                  </section>

                  <section className="admin-ops__section">
                    <div className="admin-ops__section-title">
                      <h3>쿠폰 발급 및 안내</h3>
                      <p>학교 코드 뒤에 0001부터 붙이고, 같은 학교 재발급은 다음 번호부터 자동으로 이어져요.</p>
                    </div>
                    {(busyAction === 'coupon-preview' || busyAction === 'coupon' || busyAction === 'coupon-resend') && (
                      <SectionLoading message={busyMessage} />
                    )}
                    {actionError.coupon && <div className="admin-ops__section-error">{actionError.coupon}</div>}
                    <div className="admin-ops__coupon-grid">
                      <label>시리즈명<input value={couponForm.seriesName} onChange={(e) => setCouponForm((p) => ({ ...p, seriesName: e.target.value }))} placeholder="비워두면 학교명 기반 자동 생성" /></label>
                      <label>개월<select value={couponForm.benefitMonths} onChange={(e) => setCouponForm((p) => ({ ...p, benefitMonths: Number(e.target.value) }))}>{couponMonthOptions.map((month) => <option key={month} value={month}>{month}개월{month === 12 ? ' (365일)' : ''}</option>)}</select></label>
                      <label>직접 개월 입력<input type="number" min="1" max="24" value={couponForm.benefitMonths} onChange={(e) => setCouponForm((p) => ({ ...p, benefitMonths: e.target.value }))} /></label>
                      <label>발행 개수<input type="number" min="1" max="5000" value={couponForm.count} onChange={(e) => setCouponForm((p) => ({ ...p, count: e.target.value }))} placeholder={`${selected.quantity || 1}`} /></label>
                      <label>쿠폰 1개당 최대 사용 횟수<input type="number" min="1" max="1000" value={couponForm.maxUsesPerCoupon} onChange={(e) => setCouponForm((p) => ({ ...p, maxUsesPerCoupon: e.target.value }))} /></label>
                      <label className="admin-ops__wide-label">설명<textarea value={couponForm.description} onChange={(e) => setCouponForm((p) => ({ ...p, description: e.target.value }))} placeholder="예: 제주여상 PRO 12개월권 13개 발급" /></label>
                      <label className="admin-ops__switch"><input type="checkbox" checked={couponForm.sendEmail} onChange={(e) => setCouponForm((p) => ({ ...p, sendEmail: e.target.checked }))} /> 안내 메일 보내기</label>
                    </div>
                    <p className="admin-ops__hint">같은 시리즈명은 이전 발급 번호를 보고 다음 번호부터 이어집니다. 예: WQABC-0001~0005 발급 후 같은 시리즈로 3개를 더 발급하면 WQABC-0006~0008로 만들어져요.</p>
                    <div className="admin-ops__action-row">
                      <button
                        className={`admin-ops__secondary ${busyAction === 'coupon-preview' ? 'admin-ops__button-spinner' : ''}`}
                        onClick={async () => {
                          clearScopedError('coupon');
                          setBusyAction('coupon-preview');
                          try {
                            const actionCaseId = selected.id;
                            const updated = await persistRecipientForAction();
                            assertCaseStillSelected(actionCaseId);
                            const res = await issueAdminOperationCoupons(updated.id, {
                              ...couponForm,
                              count: couponForm.count || updated.quantity,
                              benefitMonths: couponForm.benefitMonths || updated.duration_months || 12,
                              dryRun: true,
                            });
                            assertCaseStillSelected(actionCaseId);
                            setPreviewModal({ type: 'coupon', title: '쿠폰 발급 전 확인', coupon: res });
                          } catch (err) {
                            if (err?.isStaleAdminOpsCase) return;
                            showScopedError('coupon', '쿠폰 발급 전 확인을 하지 못했어요.', err);
                          } finally {
                            setBusyAction('');
                          }
                        }}
                        disabled={busyAction === 'coupon' || busyAction === 'coupon-preview' || busyAction === 'coupon-resend'}
                      >
                        {busyAction === 'coupon-preview' ? '확인 중...' : '발급 전 확인'}
                      </button>
                      <button className={`admin-ops__danger ${busyAction === 'coupon' ? 'admin-ops__button-spinner' : ''}`} onClick={() => issueCoupons(false)} disabled={busyAction === 'coupon' || busyAction === 'coupon-resend'}>{busyAction === 'coupon' ? '발급 중...' : '실제 쿠폰 발급하기'}</button>
                      <button className={`admin-ops__secondary ${busyAction === 'coupon-resend' ? 'admin-ops__button-spinner' : ''}`} onClick={resendCouponEmail} disabled={busyAction === 'coupon' || busyAction === 'coupon-preview' || busyAction === 'coupon-resend' || !selected.coupon_codes?.length}>
                        {busyAction === 'coupon-resend' ? '재발송 중...' : '재발송하기'}
                      </button>
                    </div>
                    {selected.coupon_codes?.length > 0 && <p className="admin-ops__hint">발급된 쿠폰: {selected.coupon_codes.join(', ')}</p>}
                  </section>

                </>
              )}
            </>
          )}
        </main>
      </div>
      {previewModal && (
        <div className="admin-ops__modal" onClick={() => setPreviewModal(null)}>
          <div className={`admin-ops__modal-card ${previewModal.type === 'document' ? 'admin-ops__modal-card--document' : ''}`} onClick={(e) => e.stopPropagation()}>
            <div className="admin-ops__modal-head">
              <h3>{previewModal.title}</h3>
              <button onClick={() => setPreviewModal(null)}>닫기</button>
            </div>

            {previewModal.type === 'document' && (
              <>
                {isPdfDocument(previewModal.doc) ? (
                  <iframe
                    title={previewModal.doc.filename}
                    src={getDocumentPreviewSrc(previewModal.doc)}
                    className="admin-ops__pdf-frame"
                  />
                ) : (
                  <p className="admin-ops__hint">한글(.hwpx) 원본은 브라우저에서 미리 볼 수 없어요. 내려받아 한글에서 열어 주세요.</p>
                )}
                <button className="admin-ops__primary" onClick={() => downloadBase64(previewModal.doc)}>
                  {isPdfDocument(previewModal.doc) ? 'PDF 내려받기' : '한글 파일 내려받기'}
                </button>
              </>
            )}

            {previewModal.type === 'timeline' && (
              <div className="admin-ops__timeline-preview">
                <p><strong>처리 내용</strong> {previewModal.event.message}</p>
                <p><strong>처리자</strong> {previewModal.event.actor_email || '-'}</p>
                <p><strong>시각</strong> {formatDate(getTimelineTime(previewModal.event))}</p>
                {getTimelineDetailRows(previewModal.event).map(([label, value]) => (
                  <p key={label}><strong>{label}</strong> {value}</p>
                ))}
                {getTimelineDetailRows(previewModal.event).length === 0 && (
                  <p><strong>상세</strong> 추가 상세 기록이 없는 처리입니다.</p>
                )}
                {previewModal.relatedNote && (
                  <div className="admin-ops__linked-email">
                    <strong>연결된 메모</strong>
                    <div className="admin-ops__email-html">
                      {(previewModal.relatedNote.body || '').split('\n').map((line, index) => (
                        <React.Fragment key={`${line}-${index}`}>
                          {line}
                          <br />
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                )}
                {previewModal.relatedEmail && (
                  <div className="admin-ops__linked-email">
                    <strong>연결된 메일 본문</strong>
                    <div
                      className="admin-ops__email-html"
                      dangerouslySetInnerHTML={{
                        __html: previewModal.relatedEmail.html || (previewModal.relatedEmail.text || '').replace(/\n/g, '<br>'),
                      }}
                    />
                  </div>
                )}
              </div>
            )}

            {previewModal.type === 'coupon' && (
              <div className="admin-ops__coupon-preview">
                <p><strong>발급 개수</strong> {previewModal.coupon.count}개</p>
                <p><strong>쿠폰 접두사</strong> {previewModal.coupon.prefix}</p>
                <p><strong>시작 번호</strong> {String(previewModal.coupon.start_suffix || 1).padStart(4, '0')}</p>
                <p>
                  <strong>이번 발급 범위</strong>{' '}
                  {previewModal.coupon.prefix}-{String(previewModal.coupon.start_suffix || 1).padStart(4, '0')}
                  ~
                  {previewModal.coupon.prefix}-{String((previewModal.coupon.start_suffix || 1) + (previewModal.coupon.count || 1) - 1).padStart(4, '0')}
                </p>
                <p><strong>기간</strong> {previewModal.coupon.benefit_months}개월</p>
                <p><strong>혜택 일수</strong> {previewModal.coupon.benefit_days}일</p>
                <p><strong>쿠폰 1개당 최대 사용</strong> {previewModal.coupon.max_uses_per_coupon}회</p>
                {previewModal.coupon.description && <p><strong>설명</strong> {previewModal.coupon.description}</p>}
                <div className="admin-ops__coupon-codes">
                  {(previewModal.coupon.coupons || []).map((coupon) => <code key={coupon.code}>{coupon.code} · {coupon.max_uses}회</code>)}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOperationsPage;
